/**
 * AriaCore - Main entry point for the Aria SDK
 * 
 * This class provides a simple, function-based API for initializing and controlling
 * the Aria AI assistant. All session management is handled internally.
 * 
 * @example
 * ```typescript
 * import { Aria } from '@centive/aria-sdk';
 * 
 * // Initialize the SDK
 * Aria.init({
 *   websocketUrl: 'wss://your-server.com/ws',
 *   userId: 'user_123',
 *   theme: 'light',
 *   onError: (err) => console.error(err),
 * });
 * 
 * // Programmatic control
 * Aria.open();   // Open the assistant
 * Aria.close();  // Close the assistant
 * Aria.destroy(); // Cleanup when done
 * ```
 */

import { AriaSessionManager } from './AriaSessionManager';
import type { AriaInitConfig, AriaSDKConfig, Theme } from '@/types';

// ============================================================================
// Internal State
// ============================================================================

interface AriaCoreState {
  initialized: boolean;
  config: AriaInitConfig | null;
  sessionManager: AriaSessionManager | null;
  uiContainer: HTMLDivElement | null;
  reactRoot: unknown | null; // React root for rendering
}

const state: AriaCoreState = {
  initialized: false,
  config: null,
  sessionManager: null,
  uiContainer: null,
  reactRoot: null,
};

// ============================================================================
// AriaCore Class (Static Methods Only)
// ============================================================================

export class AriaCore {
  /**
   * Initialize the Aria SDK with the given configuration.
   * This sets up the WebSocket connection, session management, and UI components.
   * 
   * @param config - Configuration options for the SDK
   * @throws Error if already initialized (call destroy() first)
   * 
   * @example
   * ```typescript
   * Aria.init({
   *   websocketUrl: 'wss://your-server.com/ws',
   *   userId: 'user_123',
   *   theme: 'dark',
   *   triggerLabel: 'Help',
   *   onError: (err) => console.error('Aria error:', err),
   * });
   * ```
   */
  static init(config: AriaInitConfig): void {
    if (state.initialized) {
      console.warn('[AriaCore] Already initialized. Call Aria.destroy() before re-initializing.');
      return;
    }

    // Validate required fields
    if (!config.websocketUrl) {
      throw new Error('[AriaCore] websocketUrl is required');
    }
    if (!config.userId) {
      throw new Error('[AriaCore] userId is required');
    }

    console.log('[AriaCore] Initializing Aria SDK...');
    state.config = config;

    // Convert simplified config to full SDK config
    const fullConfig: AriaSDKConfig = AriaCore.buildFullConfig(config);

    // Initialize session manager (singleton)
    state.sessionManager = AriaSessionManager.getInstance(fullConfig);

    // Auto-connect WebSocket
    state.sessionManager.connect();

    // Mount UI components to DOM
    AriaCore.mountUI(config.theme || 'light', config.triggerLabel);

    state.initialized = true;
    console.log('[AriaCore] Aria SDK initialized successfully');
  }

  /**
   * Open the Aria assistant widget.
   * 
   * @param mode - Optional trigger mode ('user' for bottom-right, 'websocket' for center)
   * 
   * @example
   * ```typescript
   * Aria.open();           // Open in default position
   * Aria.open('websocket'); // Open in center (like incoming call)
   * ```
   */
  static open(mode: 'user' | 'websocket' = 'user'): void {
    if (!state.initialized) {
      console.error('[AriaCore] Not initialized. Call Aria.init() first.');
      return;
    }

    // Dispatch custom event that the mounted React component listens to
    const event = new CustomEvent('aria:open', { detail: { mode } });
    window.dispatchEvent(event);
  }

  /**
   * Close the Aria assistant widget.
   * 
   * @example
   * ```typescript
   * Aria.close();
   * ```
   */
  static close(): void {
    if (!state.initialized) {
      console.error('[AriaCore] Not initialized. Call Aria.init() first.');
      return;
    }

    // Dispatch custom event that the mounted React component listens to
    const event = new CustomEvent('aria:close');
    window.dispatchEvent(event);
  }

  /**
   * Minimize the Aria assistant widget to a compact floating state.
   * 
   * @example
   * ```typescript
   * Aria.minimize();
   * ```
   */
  static minimize(): void {
    if (!state.initialized) {
      console.error('[AriaCore] Not initialized. Call Aria.init() first.');
      return;
    }

    const event = new CustomEvent('aria:minimize');
    window.dispatchEvent(event);
  }

  /**
   * Maximize the Aria assistant widget from minimized state.
   * 
   * @example
   * ```typescript
   * Aria.maximize();
   * ```
   */
  static maximize(): void {
    if (!state.initialized) {
      console.error('[AriaCore] Not initialized. Call Aria.init() first.');
      return;
    }

    const event = new CustomEvent('aria:maximize');
    window.dispatchEvent(event);
  }

  /**
   * Check if the Aria SDK is initialized.
   * 
   * @returns true if initialized, false otherwise
   */
  static isInitialized(): boolean {
    return state.initialized;
  }

  /**
   * Check if the assistant widget is currently open.
   * 
   * @returns true if open, false otherwise
   */
  static isOpen(): boolean {
    if (!state.initialized) return false;
    // Check via DOM attribute set by React component
    return state.uiContainer?.getAttribute('data-aria-open') === 'true';
  }

  /**
   * Get the current session ID if available.
   * 
   * @returns Session ID or null if no active session
   */
  static getSessionId(): string | null {
    if (!state.sessionManager) return null;
    return state.sessionManager.getSessionState().session_id;
  }

  /**
   * Get the current connection status.
   * 
   * @returns true if WebSocket is connected, false otherwise
   */
  static isConnected(): boolean {
    if (!state.sessionManager) return false;
    return state.sessionManager.isConnected();
  }

  /**
   * Manually refresh the session token.
   * Usually not needed as the SDK auto-refreshes before expiry.
   * 
   * @example
   * ```typescript
   * await Aria.refreshSession();
   * ```
   */
  static async refreshSession(): Promise<void> {
    if (!state.sessionManager) {
      console.error('[AriaCore] Not initialized. Call Aria.init() first.');
      return;
    }
    await state.sessionManager.refreshSession();
  }

  /**
   * Destroy the Aria SDK instance and clean up all resources.
   * Call this when you want to completely remove Aria from your app.
   * 
   * @example
   * ```typescript
   * Aria.destroy();
   * ```
   */
  static destroy(): void {
    if (!state.initialized) {
      console.log('[AriaCore] Not initialized, nothing to destroy.');
      return;
    }

    console.log('[AriaCore] Destroying Aria SDK...');

    // Unmount React UI
    AriaCore.unmountUI();

    // Destroy session manager singleton
    AriaSessionManager.destroyInstance();

    // Reset state
    state.initialized = false;
    state.config = null;
    state.sessionManager = null;
    state.uiContainer = null;
    state.reactRoot = null;

    console.log('[AriaCore] Aria SDK destroyed');
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  /**
   * Convert simplified AriaInitConfig to full AriaSDKConfig
   */
  private static buildFullConfig(config: AriaInitConfig): AriaSDKConfig {
    return {
      websocketUrl: config.websocketUrl,
      userId: config.userId,
      theme: config.theme || 'light',
      triggerLabel: config.triggerLabel,
      onError: config.onError,
      
      // Enable all automatic session management features
      preloadSession: true,
      sessionRefreshBuffer: 5, // 5 minutes before expiry
      persistSession: true,    // Keep session alive when widget closes
      keepWebSocketAlive: true,
      
      // Internal callbacks for session management
      onSessionReady: () => {
        console.log('[AriaCore] Session ready');
      },
      onConnectionStateChange: (connected) => {
        console.log('[AriaCore] Connection state:', connected ? 'connected' : 'disconnected');
      },
      onSessionPreloaded: () => {
        console.log('[AriaCore] Session preloaded and ready');
      },
      onSessionRefresh: () => {
        console.log('[AriaCore] Session refreshed');
      },
      onSessionRefreshError: (error) => {
        console.error('[AriaCore] Session refresh error:', error);
        config.onError?.(error);
      },
      
      // UI options - SDK manages these internally
      showFloatingButton: true,
      showAssistant: true,
    };
  }

  /**
   * Mount the Aria UI components to the DOM
   */
  private static mountUI(theme: Theme, triggerLabel?: string): void {
    // Create container element
    const container = document.createElement('div');
    container.id = 'aria-sdk-root';
    container.setAttribute('data-aria-theme', theme);
    container.setAttribute('data-aria-open', 'false');
    document.body.appendChild(container);
    state.uiContainer = container;

    // Dynamically import React and render the component
    // This allows the SDK to work without requiring React in the consuming app's bundle
    AriaCore.renderReactUI(container, theme, triggerLabel);
  }

  /**
   * Render React UI components
   */
  private static async renderReactUI(
    container: HTMLElement, 
    theme: Theme, 
    triggerLabel?: string
  ): Promise<void> {
    try {
      // Dynamic imports to avoid bundling React if not needed
      const React = await import('react');
      const ReactDOM = await import('react-dom/client');
      const { AriaStandaloneUI } = await import('@/components/AriaStandaloneUI');

      const root = ReactDOM.createRoot(container);
      state.reactRoot = root;

      root.render(
        React.createElement(AriaStandaloneUI, {
          theme,
          triggerLabel,
          container,
        })
      );

      console.log('[AriaCore] React UI mounted');
    } catch (error) {
      console.error('[AriaCore] Failed to mount React UI:', error);
      state.config?.onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  }

  /**
   * Unmount the Aria UI components from the DOM
   */
  private static unmountUI(): void {
    if (state.reactRoot && typeof (state.reactRoot as { unmount?: () => void }).unmount === 'function') {
      (state.reactRoot as { unmount: () => void }).unmount();
    }

    if (state.uiContainer) {
      state.uiContainer.remove();
    }
  }
}

// Export as default for convenient importing
export default AriaCore;
